import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  Activity, Radio, Database, Share2, Lightbulb, Shield, TrendingUp, TrendingDown, Target,
  ArrowRight, X, Sparkles, ChevronRight, ChevronDown, Info, ArrowUpRight, Search, Minus,
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AddToDashboardButton } from '../components/AddToDashboardButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: '#021735', surface: '#09254D', surface2: '#0D2C55', border: '#25568F',
  cyan: '#29E6F4', blue: '#339AE5', electric: '#506FEF', purple: '#8358F5',
  teal: '#2DDBA5', gold: '#F2C94C', pink: '#F05A87',
  text: '#F7FAFF', text2: '#D4E1F2', muted: '#9FB6D1',
};
const TONE = { cyan: C.cyan, blue: C.blue, purple: C.purple, teal: C.teal };
const KIND = { rising: C.teal, declining: C.pink, durable: C.cyan, emerging: C.purple };

const SHIFT = {
  high_automation: { label: 'Automating Fast', hex: C.pink },
  high_augmentation: { label: 'AI-Accelerated', hex: C.cyan },
  moderate_augmentation: { label: 'Hybrid', hex: C.blue },
  durable: { label: 'Human-Led', hex: C.teal },
  highly_durable: { label: 'Human-Led', hex: C.teal },
};

const PULSE_CSS = `
@keyframes cp-glow { 0%,100% { filter: drop-shadow(0 0 4px var(--g)); } 50% { filter: drop-shadow(0 0 12px var(--g)); } }
@keyframes cp-ping { 0% { transform: scale(1); opacity: .7; } 70%,100% { transform: scale(2.4); opacity: 0; } }
@keyframes cp-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
.cp-ring-glow { animation: cp-glow 3s ease-in-out infinite; }
.cp-marquee { animation: cp-marquee 42s linear infinite; }
.cp-marquee:hover { animation-play-state: paused; }
`;

function Ring({ value, hex }) {
  const r = 34, circ = 2 * Math.PI * r, off = circ * (1 - (value || 0) / 100);
  return (
    <div className="relative w-[86px] h-[86px] shrink-0">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90 cp-ring-glow" style={{ '--g': `${hex}aa` }}>
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={hex} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset 1.1s ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold fp-num" style={{ color: C.text }}>{value}%</span>
      </div>
    </div>
  );
}

// A real trend line with an area fill, not a dash.
function Spark({ data, hex, up }) {
  if (!data?.length) return null;
  const w = 150, h = 40, pad = 3;
  const max = Math.max(...data), min = Math.min(...data), span = Math.max(1, max - min);
  const xy = data.map((v, i) => [(i / (data.length - 1)) * w, h - pad - ((v - min) / span) * (h - pad * 2)]);
  const line = xy.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;
  const id = `sg-${hex.replace('#', '')}-${up ? 'u' : 'd'}`;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="mt-2">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hex} stopOpacity="0.35" />
          <stop offset="100%" stopColor={hex} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${id})`} />
      <polyline points={line} fill="none" stroke={hex} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 5px ${hex})` }} />
      <circle cx={xy[xy.length - 1][0]} cy={xy[xy.length - 1][1]} r="3" fill={hex} style={{ filter: `drop-shadow(0 0 5px ${hex})` }} />
    </svg>
  );
}

function TrendArrow({ dir, hex }) {
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  const col = dir === 'up' ? C.teal : dir === 'down' ? C.pink : C.muted;
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: col }}><Icon className="w-3.5 h-3.5" style={{ color: hex }} />{dir === 'up' ? 'Rising' : dir === 'down' ? 'Cooling' : 'Steady'}</span>;
}

function Pill({ icon: Icon, label, value, onClick, testid, live }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className="flex items-center gap-2 rounded-xl px-3 py-2 border text-left transition-colors"
      style={{ background: C.surface, borderColor: C.border, cursor: onClick ? 'pointer' : 'default' }}>
      <span className="relative flex items-center justify-center w-4 h-4 shrink-0">
        {live && <span className="absolute inline-flex w-2.5 h-2.5 rounded-full" style={{ background: C.teal, animation: 'cp-ping 1.8s cubic-bezier(0,0,.2,1) infinite' }} />}
        <Icon className="w-4 h-4" style={{ color: C.cyan }} />
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-wide" style={{ color: C.muted }}>{label}</p>
        <p className="text-[13px] font-semibold" style={{ color: C.text }}>{value}</p>
      </div>
    </button>
  );
}

function Ticker({ signals }) {
  if (!signals?.length) return null;
  const row = [...signals, ...signals];
  return (
    <div className="relative overflow-hidden rounded-xl border py-2" style={{ background: C.surface, borderColor: C.border }} data-testid="live-ticker">
      <div className="flex items-center gap-2 px-3 absolute left-0 top-0 bottom-0 z-10" style={{ background: `linear-gradient(90deg, ${C.surface} 70%, transparent)` }}>
        <span className="relative flex w-2 h-2"><span className="absolute inline-flex w-2 h-2 rounded-full" style={{ background: C.pink, animation: 'cp-ping 1.6s cubic-bezier(0,0,.2,1) infinite' }} /><span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: C.pink }} /></span>
        <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: C.pink }}>Live</span>
      </div>
      <div className="flex gap-8 whitespace-nowrap cp-marquee pl-20" style={{ width: 'max-content' }}>
        {row.map((s, i) => {
          const hex = KIND[s.kind] || C.cyan;
          return (
            <span key={i} className="inline-flex items-center gap-2 text-[13px]" style={{ color: C.text }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: hex, boxShadow: `0 0 6px ${hex}` }} />
              {s.text}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ItemBar({ it }) {
  const hex = KIND[it.kind] || C.cyan;
  const pct = it.value ? Math.min(100, Math.abs(it.value)) : null;
  return (
    <div data-testid={`detail-item-${it.label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between text-sm">
        <span style={{ color: C.text }}>{it.label}</span>
        {it.value != null && (
          <span className="fp-num font-semibold text-[12px]" style={{ color: hex }}>
            {it.kind === 'declining' ? '' : it.kind === 'durable' ? '' : '+'}{it.value}{it.kind === 'durable' ? '% durable' : '%'}
          </span>
        )}
        {it.value == null && <span className="text-[11px] px-2 py-0.5 rounded-full capitalize" style={{ background: `${hex}1f`, color: hex }}>{it.kind}</span>}
      </div>
      {pct != null && (
        <div className="mt-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: hex, boxShadow: `0 0 8px ${hex}88` }} />
        </div>
      )}
      {it.note && <p className="text-[12px] mt-1" style={{ color: C.text2 }}>{it.note}</p>}
    </div>
  );
}

function MetricDrawer({ metric, onClose }) {
  if (!metric) return null;
  const hex = TONE[metric.tone] || C.cyan;
  const d = metric.detail || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.65)' }} onClick={onClose} data-testid="metric-drawer">
      <div className="rounded-2xl p-6 max-w-lg w-full border max-h-[86vh] overflow-y-auto" style={{ background: C.surface, borderColor: `${hex}55`, boxShadow: `0 0 40px ${hex}44` }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Ring value={metric.value} hex={hex} />
            <div>
              <h3 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>{metric.label}</h3>
              <div className="mt-1"><TrendArrow dir={metric.direction} hex={hex} /></div>
            </div>
          </div>
          <button onClick={onClose} data-testid="metric-drawer-close" style={{ color: C.muted }}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-[12px] mt-3 rounded-lg px-3 py-2 border" style={{ color: C.text2, background: `${hex}10`, borderColor: `${hex}33` }}>
          <span className="font-semibold" style={{ color: hex }}>How this is measured: </span>{metric.how}
        </p>
        {d.narrative && <p className="text-sm mt-3 leading-relaxed" style={{ color: C.text2 }}>{d.narrative}</p>}
        {d.items?.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>
              {metric.key === 'human' ? 'Your durable edge' : metric.key === 'automation' ? 'Skills to move away from' : metric.key === 'resilience' ? 'Where hiring is growing' : 'Skills to build now'}
            </p>
            <div className="space-y-3">{d.items.map((it, i) => <ItemBar key={i} it={it} />)}</div>
          </div>
        )}
        {d.tasks?.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>Tasks affected</p>
            <div className="flex flex-wrap gap-2">
              {d.tasks.map((t, i) => <span key={i} className="text-[12px] px-2.5 py-1 rounded-full border" style={{ color: C.text2, borderColor: C.border, background: C.surface2 }}>{t}</span>)}
            </div>
          </div>
        )}
        {d.action && (
          <Link to={d.action.path} data-testid="metric-drawer-action"
            className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-bold" style={{ color: C.text, background: `linear-gradient(90deg,${hex},${C.electric})`, boxShadow: `0 0 18px ${hex}55` }}>
            {d.action.label} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

export default function FutureOfWorkPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [activeMetric, setActiveMetric] = useState(null);
  const [openTask, setOpenTask] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/college/future-of-work-lab`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-20" style={{ color: C.text2 }}>Unable to load Career Pulse.</div></AppLayout>;

  const { student, career_pulse, next_moves, live_signals = [], task_impact = [], emerging_paths = [], personal_insight, playbook = [], sources = [] } = data;
  const target = student?.career_target || 'your career';
  const meta = career_pulse?.meta || {};
  const metrics = career_pulse?.metrics || [];
  const researchUrl = (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}`;

  return (
    <AppLayout>
      <style>{PULSE_CSS}</style>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="career-pulse">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl p-6 md:p-7 border"
          style={{ background: `linear-gradient(120deg, ${C.surface}, ${C.bg})`, borderColor: C.border }}>
          <div className="absolute -top-20 -right-16 w-96 h-96 rounded-full blur-3xl opacity-40" style={{ background: `radial-gradient(circle, ${C.cyan}, transparent 65%)` }} />
          <div className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full blur-3xl opacity-25" style={{ background: `radial-gradient(circle, ${C.purple}, transparent 65%)` }} />
          <div className="relative flex flex-wrap items-start justify-between gap-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] flex items-center gap-2" style={{ color: C.cyan }}>
                <span className="relative flex w-2 h-2"><span className="absolute inline-flex w-2 h-2 rounded-full" style={{ background: C.cyan, animation: 'cp-ping 1.8s cubic-bezier(0,0,.2,1) infinite' }} /><span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: C.cyan }} /></span>
                Live Intelligence
              </p>
              <h1 className="font-outfit text-4xl sm:text-5xl font-bold mt-1" style={{ color: C.text }}>Career Pulse</h1>
              <p className="text-base mt-2 max-w-2xl" style={{ color: C.text2 }}>
                This is how AI is changing the future of work and how it impacts you. This page diagnoses your path, tracking how AI is reshaping <span style={{ color: C.cyan }}>{target}</span> right now.
              </p>
              <Link to="/app/career-intelligence" data-testid="career-focus-chip"
                className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-sm font-semibold border"
                style={{ background: `${C.cyan}14`, borderColor: `${C.cyan}55`, color: C.text }}>
                <Target className="w-3.5 h-3.5" style={{ color: C.cyan }} /> {target}
                <span className="text-[11px]" style={{ color: C.muted }}>Your career focus</span>
              </Link>
            </div>
            <div className="hidden lg:block text-right">
              <p className="font-outfit text-sm font-bold leading-tight" style={{ color: C.cyan }}>REAL TRENDS.</p>
              <p className="font-outfit text-sm font-bold leading-tight" style={{ color: C.blue }}>REAL INSIGHTS.</p>
              <p className="font-outfit text-sm font-bold leading-tight" style={{ color: C.purple }}>A BRIGHTER YOU.</p>
            </div>
          </div>
          <div className="relative flex flex-wrap gap-2.5 mt-5">
            <Pill icon={Activity} label="Last updated" value={meta.last_updated || '-'} testid="pill-updated" live />
            <Pill icon={TrendingUp} label="Market pulse" value={meta.market_pulse || '-'} testid="pill-pulse" />
            <Pill icon={Database} label="Data freshness" value={meta.data_freshness || '-'} testid="pill-fresh" />
            <Pill icon={Share2} label="Sources monitored" value={`${meta.sources_count || sources.length} sources`} onClick={() => setSourcesOpen(true)} testid="pill-sources" />
          </div>
        </div>

        {/* Live ticker */}
        <Ticker signals={live_signals} />

        {/* Pulse metric rings (clickable) */}
        {metrics.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pulse-metrics">
            {metrics.map((m) => {
              const hex = TONE[m.tone] || C.cyan;
              return (
                <button key={m.key} onClick={() => setActiveMetric(m)} data-testid={`metric-${m.key}`}
                  className="text-left rounded-2xl p-4 border transition-transform hover:-translate-y-1 relative group"
                  style={{ background: C.surface, borderColor: C.border }}>
                  <span className="absolute top-3 right-3 opacity-60 group-hover:opacity-100" style={{ color: hex }}><Info className="w-4 h-4" /></span>
                  <div className="flex items-center gap-3 pr-5">
                    <Ring value={m.value} hex={hex} />
                    <div>
                      <p className="text-sm font-semibold leading-snug" style={{ color: C.text }}>{m.label}</p>
                      <div className="mt-1"><TrendArrow dir={m.direction} hex={hex} /></div>
                    </div>
                  </div>
                  <Spark data={m.spark} hex={hex} up={m.direction !== 'down'} />
                  <p className="text-[12px] mt-1.5" style={{ color: C.text2 }}>{m.sub}</p>
                  <p className="text-[11px] mt-2 font-semibold inline-flex items-center gap-1" style={{ color: hex }}>Tap for details <ChevronRight className="w-3 h-3" /></p>
                </button>
              );
            })}
          </div>
        )}

        {/* Two-column body */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEFT */}
          <div className="space-y-6">
            {personal_insight && (
              <div className="rounded-2xl p-6 border" style={{ background: C.surface, borderColor: C.border }} data-testid="what-this-means">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" style={{ color: C.gold }} />
                  <h2 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>What This Means for You</h2>
                </div>
                {student?.major && student?.academic_year && (
                  <p className="text-[12px] mt-1" style={{ color: C.muted }}>Personalized for a {student.major} {student.academic_year}</p>
                )}
                <p className="text-sm mt-3 leading-relaxed" style={{ color: C.text2 }}>{personal_insight}</p>
                <div className="mt-4 rounded-xl p-4 border" style={{ background: `${C.teal}14`, borderColor: `${C.teal}44` }}>
                  <p className="text-sm font-semibold" style={{ color: C.teal }}>Good news</p>
                  <p className="text-[13px] mt-1" style={{ color: C.text2 }}>Your human skills are your superpower. Combine your background with data and AI to stand out.</p>
                </div>
              </div>
            )}

            {playbook.length > 0 && (
              <div className="rounded-2xl p-6 border" style={{ background: C.surface, borderColor: C.border }} data-testid="durable-edge">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: C.cyan }} />
                  <h2 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Your Durable Edge</h2>
                </div>
                <p className="text-[12px] mt-1" style={{ color: C.text2 }}>Skills that stay valuable and differentiate you as AI advances.</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                  {playbook.map((p) => (
                    <div key={p.skill} data-testid={`edge-${p.skill.replace(/\s+/g, '-').toLowerCase()}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: C.text }}>{p.skill}</span>
                        <span className="fp-num font-semibold" style={{ color: C.cyan }}>{p.durability}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <div className="h-full rounded-full" style={{ width: `${p.durability}%`, background: `linear-gradient(90deg,${C.cyan},${C.teal})`, boxShadow: `0 0 10px ${C.cyan}66` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {emerging_paths.length > 0 && (
              <div className="rounded-2xl p-6 border" style={{ background: C.surface, borderColor: C.border }} data-testid="emerging-paths">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" style={{ color: C.purple }} />
                    <h2 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Emerging Paths</h2>
                  </div>
                  <Link to="/app/career-landscape" className="text-sm inline-flex items-center gap-1" style={{ color: C.cyan }} data-testid="explore-all-careers">Explore all <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
                <p className="text-[12px] mt-1" style={{ color: C.text2 }}>Adjacent and rising opportunities in the {target} ecosystem.</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {emerging_paths.map((e) => {
                    const growing = (e.trend || '').toLowerCase() === 'growing';
                    const hex = growing ? C.teal : C.purple;
                    return (
                      <a key={e.title} href={researchUrl(`${e.title} career ${target}`)} target="_blank" rel="noopener noreferrer"
                        className="rounded-xl p-4 border block hover:-translate-y-0.5 transition-transform" style={{ background: C.surface2, borderColor: C.border }} data-testid={`path-${e.title.replace(/\s+/g, '-').toLowerCase()}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold" style={{ color: C.text }}>{e.title}</p>
                          <span className="text-[10px] px-2 py-0.5 rounded-full capitalize" style={{ background: `${hex}1f`, color: hex }}>{e.trend}</span>
                        </div>
                        <p className="text-[12px] mt-1.5" style={{ color: C.text2 }}>{e.why}</p>
                        <span className="text-[11px] mt-2 inline-flex items-center gap-1" style={{ color: C.cyan }}>Research this <ArrowUpRight className="w-3 h-3" /></span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT */}
          <div className="space-y-6">
            {task_impact.length > 0 && (
              <div className="rounded-2xl p-6 border" style={{ background: C.surface, borderColor: C.border }} data-testid="task-shift-map">
                <div className="flex items-center gap-2">
                  <Radio className="w-5 h-5" style={{ color: C.cyan }} />
                  <h2 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Live Task Shift Map</h2>
                </div>
                <p className="text-[13px] mt-1.5" style={{ color: C.text2 }}>
                  A day in this role, task by task, showing what AI is <span style={{ color: C.pink }}>automating</span>, <span style={{ color: C.cyan }}>accelerating</span>, or <span style={{ color: C.teal }}>leaving to you</span>. Tap any task to see what it means and how to prepare.
                </p>
                <div className="mt-4 space-y-2">
                  {task_impact.map((t) => {
                    const s = SHIFT[t.impact] || { label: t.label || 'Shifting', hex: C.blue };
                    const open = openTask === t.task;
                    return (
                      <div key={t.task} className="rounded-xl border overflow-hidden" style={{ borderColor: open ? `${s.hex}66` : C.border, background: C.surface2 }} data-testid={`task-${t.task.replace(/\s+/g, '-').toLowerCase().slice(0, 20)}`}>
                        <button onClick={() => setOpenTask(open ? null : t.task)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                          <span className="text-[14px] font-medium" style={{ color: C.text }}>{t.task}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap font-semibold" style={{ background: `${s.hex}22`, color: s.hex, boxShadow: `0 0 10px ${s.hex}44` }}>{s.label}</span>
                            <ChevronDown className="w-4 h-4 transition-transform" style={{ color: C.muted, transform: open ? 'rotate(180deg)' : 'none' }} />
                          </span>
                        </button>
                        {open && (
                          <div className="px-4 pb-3 -mt-1">
                            <p className="text-[13px]" style={{ color: C.text2 }}>{t.detail}</p>
                            <a href={researchUrl(`how is AI changing ${t.task} in ${target}`)} target="_blank" rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: C.cyan }} data-testid={`task-research-${t.task.replace(/\s+/g, '-').toLowerCase().slice(0, 16)}`}>
                              <Search className="w-3 h-3" /> Research this shift <ArrowUpRight className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {next_moves?.length > 0 && (
              <div className="rounded-2xl p-6 border" style={{ background: C.surface, borderColor: C.border }} data-testid="next-moves">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5" style={{ color: C.cyan }} />
                    <h2 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Your Next Moves</h2>
                  </div>
                  <Link to="/app/my-strategy" className="text-sm inline-flex items-center gap-1" style={{ color: C.cyan }} data-testid="turn-into-plan">Turn into a plan <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
                <p className="text-[12px] mt-1" style={{ color: C.text2 }}>A personalized playbook to stay ahead.</p>
                <div className="mt-4 space-y-2.5">
                  {next_moves.map((m, i) => {
                    const inner = (
                      <>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0 fp-num" style={{ background: `${C.cyan}22`, color: C.cyan }}>{i + 1}</span>
                        <div className="flex-1">
                          <p className="text-[13px] font-medium" style={{ color: C.text }}>{m.title}</p>
                          <p className="text-[12px]" style={{ color: C.text2 }}>{m.detail}</p>
                        </div>
                        {m.path && <ChevronRight className="w-4 h-4" style={{ color: C.muted }} />}
                      </>
                    );
                    return m.path ? (
                      <Link key={i} to={m.path} data-testid={`next-move-${i}`} className="flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors hover:bg-white/5" style={{ background: C.surface2, borderColor: C.border }}>{inner}</Link>
                    ) : (
                      <div key={i} data-testid={`next-move-${i}`} className="flex items-center gap-3 rounded-xl px-3 py-2.5 border" style={{ background: C.surface2, borderColor: C.border }}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="rounded-2xl p-6 border flex flex-wrap items-center justify-between gap-5" style={{ background: `linear-gradient(120deg, ${C.surface2}, ${C.surface})`, borderColor: C.border }} data-testid="pulse-footer">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 cp-ring-glow" style={{ color: C.cyan, '--g': `${C.cyan}aa` }} />
            <div>
              <h3 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Your future is changing. So are the opportunities.</h3>
              <p className="text-sm" style={{ color: C.text2 }}>Turn insights into a personalized strategy and stay ahead in an AI-powered world.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app/my-strategy" data-testid="explore-my-strategy" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-base font-bold" style={{ color: C.text, background: `linear-gradient(90deg,${C.cyan},${C.electric},${C.purple})`, boxShadow: `0 0 24px ${C.cyan}55` }}>
              Explore My Strategy <ChevronRight className="w-4 h-4" />
            </Link>
            <AddToDashboardButton moduleId="ai_impact" label="Pin to Dashboard" />
          </div>
        </div>
      </motion.div>

      <MetricDrawer metric={activeMetric} onClose={() => setActiveMetric(null)} />

      {/* Sources drawer */}
      {sourcesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setSourcesOpen(false)} data-testid="sources-drawer">
          <div className="rounded-2xl p-6 max-w-md w-full border" style={{ background: C.surface, borderColor: C.border }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Share2 className="w-5 h-5" style={{ color: C.cyan }} /><h3 className="font-outfit text-lg font-semibold" style={{ color: C.text }}>Sources monitored</h3></div>
              <button onClick={() => setSourcesOpen(false)} data-testid="sources-close" style={{ color: C.muted }}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[12px] mt-1" style={{ color: C.text2 }}>The intelligence behind this view, tuned to {target}.</p>
            <ul className="mt-4 space-y-2">
              {sources.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm" style={{ color: C.text2 }} data-testid={`source-${i}`}><Sparkles className="w-4 h-4 shrink-0" style={{ color: C.cyan }} />{s}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
